"use client";

import {
	INBOUND_POSTHOG_PROPERTY_KEYS,
	buildInboundPostHogProperties,
	getInboundAttribution,
} from "@/lib/inbound-attribution";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import posthog from "posthog-js";

function clearInboundPostHogProperties() {
	for (const key of INBOUND_POSTHOG_PROPERTY_KEYS) {
		posthog.unregister(key);
	}
}

function buildLandingPath(pathname: string | null, search: string) {
	if (pathname === null || pathname.length === 0) {
		return search.length > 0 ? `/?${search}` : "/";
	}

	return search.length > 0 ? `${pathname}?${search}` : pathname;
}

export function InboundAttributionTracker() {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();

	useEffect(() => {
		const attribution = getInboundAttribution();
		if (attribution === null) {
			clearInboundPostHogProperties();
			return;
		}

		const landingPath = buildLandingPath(pathname, search);
		const sessionStorageKey = `pbf-inbound-landing:${attribution.clickId}:${landingPath}`;
		posthog.register(buildInboundPostHogProperties(attribution));

		if (typeof window === "undefined") {
			return;
		}

		if (window.sessionStorage.getItem(sessionStorageKey) === "1") {
			return;
		}

		window.sessionStorage.setItem(sessionStorageKey, "1");
		posthog.capture("inbound_landing_viewed", {
			...buildInboundPostHogProperties(attribution),
			landing_path: landingPath,
			landing_url: window.location.href,
		});

		const posthogDistinctId = posthog.get_distinct_id();
		if (typeof posthogDistinctId !== "string" || posthogDistinctId.trim().length === 0) {
			return;
		}

		void fetch("/api/inbound/landing", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				posthogDistinctId,
				landingPath,
				landingUrl: window.location.href,
				...(document.referrer.length > 0 && { referrer: document.referrer }),
			}),
		});
	}, [pathname, search]);

	return null;
}
